import { Injectable, isDevMode } from "@angular/core";
import { Action, Selector, State, StateContext, StateToken, Store } from "@ngxs/store";
import { FinOpsAwsAccountsService } from "./finops-aws-accounts.service";
import {
    LoadFinOpsAwsAccountsHistoricalData,
    LoadDataTableFinOpsAwsAccounts,
    LoadFinOpsAwsAccountsChartsData,
    LoadFinOpsAwsAccountsChartsFilteredData
} from "./finops-aws-accounts.actions";
import { finalize, forkJoin } from "rxjs";
import { FinOpsAwsAccount, FinOpsAwsAccountsStateModel, FinOpsCounters } from "./finops-aws-accounts.model";
import { PreferencesFiltersState } from "src/app/store/preferences/filters/preferences-filters.state";
import { PreferencesFinopsState } from "src/app/store/preferences/finops/preferences-finops.state";
import { PreferencesChartsState } from "src/app/store/preferences/charts/preferences-charts.state";
import { FinOpsChartHistoricalItem } from "src/app/shared/component/chart-finops-historical/chart-finops-historical.component";
import { FinOpsChartCompareItem } from "src/app/shared/component/chart-finops-compare/chart-finops-compare.component";
import { DateGranularityEnum } from "src/app/shared/enum/date-granularity.enum";
import { ColorEnum } from "src/app/shared/enum/color.enum";
import { AwsAccountLabelPipe } from "src/app/shared/pipe/aws-account-label.pipe";
import { TranslateService } from "@ngx-translate/core";
import { DateService, HistoricalDate } from "src/app/shared/service/date.service";
import { ItemTypeEnum } from "src/app/shared/enum/item-type.enum";
import { FloorPipe } from "src/app/shared/pipe/floor.pipe";
import { FormatNumberPipe } from "src/app/shared/pipe/format-number.pipe";
import { MetaService } from "src/app/store/meta/meta.service";
import { MetaRequestHelper } from "src/app/store/meta/meta-request.helper";
import { FinOpsContextState } from "src/app/store/finops/context/finops-context.state";
import { SelectFinOpsDate } from "src/app/store/finops/context/finops-context.actions";

const FINOPS_AWS_ACCOUNTS_TOKEN: StateToken<FinOpsAwsAccountsStateModel> = new StateToken('finOpsAwsAccounts');

const FinOpsAccountsStateModelDefaults: FinOpsAwsAccountsStateModel = {
    historicalDataByGranularity: {},
    datatable: [],
    chartsDataDisplayed: {
        filteredCompareChartData: {
            minCostRatioString:"",
            minCostValueString: "",
            prediction: false,
            dateString: "",
            costs: []
        },
        filteredHistoricalChartData: {
            minCostRatioString:"",
            minCostValueString: "",
            prediction: false,
            costs: []
        }
    },
    chartsDataByDate: {},
    filteredCounters: {}
}

@State<FinOpsAwsAccountsStateModel>({
    name: FINOPS_AWS_ACCOUNTS_TOKEN,
    defaults: FinOpsAccountsStateModelDefaults
})

@Injectable()
export class FinOpsAwsAccountsState {

    private readonly KEY = FINOPS_AWS_ACCOUNTS_TOKEN.getName()

    constructor(
        private finOpsAwsAccountsService: FinOpsAwsAccountsService,
        private store: Store,
        private awsAccountLabelPipe: AwsAccountLabelPipe,
        private floorPipe: FloorPipe,
        private numberFormatPipe: FormatNumberPipe,
        private dateService: DateService,
        private translate: TranslateService,
        private metaService: MetaService
    ) {
    }

    @Selector([FINOPS_AWS_ACCOUNTS_TOKEN])
    static getDatatable(state: FinOpsAwsAccountsStateModel) {
        return state.datatable
    }

    @Selector([FINOPS_AWS_ACCOUNTS_TOKEN])
    static getFilteredCounters(state: FinOpsAwsAccountsStateModel) {
        return state.filteredCounters
    }

    @Selector([FINOPS_AWS_ACCOUNTS_TOKEN])
    static getChartsDataDisplayed(state: FinOpsAwsAccountsStateModel) {
        return state.chartsDataDisplayed
    }

    @Selector([FINOPS_AWS_ACCOUNTS_TOKEN])
    static getFilteredCompareChartData(state: FinOpsAwsAccountsStateModel) {
        return state.chartsDataDisplayed.filteredCompareChartData
    }

    @Selector([FINOPS_AWS_ACCOUNTS_TOKEN])
    static getFilteredHistoricalChartData(state: FinOpsAwsAccountsStateModel) {
        return state.chartsDataDisplayed.filteredHistoricalChartData
    }

    @Selector([FINOPS_AWS_ACCOUNTS_TOKEN])
    static getAllChartsDataByDate(state: FinOpsAwsAccountsStateModel) {
        return state.chartsDataByDate
    }

    @Selector([FINOPS_AWS_ACCOUNTS_TOKEN])
    static getChartsDataByDate(state: FinOpsAwsAccountsStateModel) {
        return (date:string) => state.chartsDataByDate[date]
    }

    @Selector([FINOPS_AWS_ACCOUNTS_TOKEN])
    static getHistoricalDataPerGranularity(state: FinOpsAwsAccountsStateModel) {
        return (granularity: string) => state.historicalDataByGranularity[granularity]
    }

    @Selector([FINOPS_AWS_ACCOUNTS_TOKEN])
    static getHistoricalDataPerGranularityPerDate(state: FinOpsAwsAccountsStateModel) {
        return (granularity: string, date: string) => state.historicalDataByGranularity[granularity][date]
    }

    @Action(LoadFinOpsAwsAccountsHistoricalData)
    loadFinOpsAwsAccountsHistoricalData(ctx: StateContext<FinOpsAwsAccountsStateModel>) {
        const finopsConfig = this.store.selectSnapshot(PreferencesFinopsState.getConfig)
        let historicalDates: HistoricalDate[] = this.dateService.getFinopsHistoricalDates(finopsConfig)
        let wrapRequestList = []

        for (let historicalDate of historicalDates) {
            const FINOPS_ACCOUNT_DATE_KEY = this.KEY
            + "-granularity-" + finopsConfig.granularity
            + "-date-" + historicalDate.date

            if(!this.metaService.isStale(FINOPS_ACCOUNT_DATE_KEY)) {
                if(isDevMode()) {
                        console.warn("Cache for KEY " + FINOPS_ACCOUNT_DATE_KEY + " still valid, no request executed.")
                }
                continue
            }

            wrapRequestList.push(
                MetaRequestHelper.wrapRequest(
                    this.store,
                    FINOPS_ACCOUNT_DATE_KEY,
                    this.finOpsAwsAccountsService.loadFinOpsAwsAccountsHistoricalData(finopsConfig.granularity, historicalDate.date),
                    resources => {
                        ctx.setState(state => {
                            const h = {...state.historicalDataByGranularity};
                            h[finopsConfig.granularity] = {
                                ...(h[finopsConfig.granularity] || {}),
                                [historicalDate.date]: resources.measures
                            };
                            return {
                                ...state,
                                historicalDataByGranularity: h
                            }
                        })
                    }
                )
            )  
        }
        return forkJoin(wrapRequestList).pipe(
            finalize( () => {
                ctx.dispatch(
                    new LoadDataTableFinOpsAwsAccounts()
                )
            }
            )
        )
    }

    @Action(LoadDataTableFinOpsAwsAccounts)
    loadDataTableFinOpsAwsAccounts(ctx: StateContext<FinOpsAwsAccountsStateModel>) {
        const finopsConfig = this.store.selectSnapshot(PreferencesFinopsState.getConfig)
        const granularity = finopsConfig.granularity
        const historicalDatesSelected = this.dateService.getFinopsHistoricalDates(finopsConfig).map(item => item.date)
        const selectedDateTile = this.store.selectSnapshot(FinOpsContextState.getSelectedDate)

        // If cannot find selected date in historical dates, select the last one and load data for it
        if (selectedDateTile === undefined || !historicalDatesSelected.includes(selectedDateTile)) {
            ctx.dispatch([
                new SelectFinOpsDate(historicalDatesSelected[historicalDatesSelected.length - 1]),
                new LoadDataTableFinOpsAwsAccounts()
            ])
            return
        }

        if (isDevMode()) {
            console.warn("Loading data for granularity " + granularity + " and date " + selectedDateTile)
        }

        let historicalDataByGranularity = ctx.getState().historicalDataByGranularity[granularity]
        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.FINOPS_AWS_ACCOUNTS]]

        let measuresDatatable: { [id: string]: FinOpsAwsAccount[]; } = {}
        let filteredCounters: { [id: string]: Record<string, FinOpsCounters>; } = {}
        let previousTotalCounters: { [id: string]: FinOpsCounters; } = {}

        for(let date of historicalDatesSelected){
                let datatable: FinOpsAwsAccount[] = []
                Object.values(historicalDataByGranularity[date]).forEach(account => {
                    datatable.push(account)
                });
                filters.forEach(filter => {
                    if (Array.isArray(filter.value)) {
                        datatable = datatable.filter((account) => filter.value.includes(account[filter.key as keyof FinOpsAwsAccount]))
                    } else {
                        datatable = datatable.filter((account) => account[filter.key as keyof FinOpsAwsAccount] === filter.value)
                    }
                });
                measuresDatatable[date] = datatable

                // Update counters for the measure
                let totalCounters: Record<DateGranularityEnum, FinOpsCounters> = {
                    year: {
                        prediction: false,
                        previous_cost: 0,
                        cost: 0,
                        day_ratio: 0,
                        gap_ratio: 0,
                        gap_value: 0
                    },
                    month: {
                        prediction: false,
                        previous_cost: 0,
                        cost: 0,
                        day_ratio: 0,
                        gap_ratio: 0,
                        gap_value: 0
                    },
                    day: {
                        prediction: false,
                        previous_cost: 0,
                        cost: 0,
                        day_ratio: 0,
                        gap_ratio: 0,
                        gap_value: 0
                    }
                }
                datatable.forEach(row => {
                    Object.entries(row.costs_counters).forEach(entries => {
                        let granularity = entries[0] as DateGranularityEnum
                        let counters = entries[1]
                        totalCounters[granularity].prediction = counters.prediction
                        totalCounters[granularity].cost += Number(counters.cost)
                        totalCounters[granularity].day_ratio = Number(counters.day_ratio)
                    });
                });

                Object.keys(totalCounters).forEach(granularity  => {
                    totalCounters[granularity as DateGranularityEnum].cost = totalCounters[granularity as DateGranularityEnum].cost
                    totalCounters[granularity as DateGranularityEnum].day_ratio = totalCounters[granularity as DateGranularityEnum].day_ratio

                    if (Object.keys(previousTotalCounters).length > 0) {
                        totalCounters[granularity as DateGranularityEnum].previous_cost = previousTotalCounters[granularity].cost
                        let gap_value = totalCounters[granularity as DateGranularityEnum].cost - totalCounters[granularity as DateGranularityEnum].previous_cost
                        totalCounters[granularity as DateGranularityEnum].gap_ratio = totalCounters[granularity as DateGranularityEnum].previous_cost == 0 ? 100 : ((gap_value / totalCounters[granularity as DateGranularityEnum].previous_cost) * 100)
                        totalCounters[granularity as DateGranularityEnum].gap_value = gap_value
                    }
                });
                filteredCounters[date] = totalCounters
                previousTotalCounters = totalCounters
            }

        ctx.patchState({
            datatable: measuresDatatable[selectedDateTile],
            filteredCounters: filteredCounters
        })
    }

    @Action(LoadFinOpsAwsAccountsChartsData)
    loadFinOpsAwsAccountsChartsData(ctx: StateContext<FinOpsAwsAccountsStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig)
        let historicalDates: HistoricalDate[] = this.dateService.getChartsHistoricalDates(chartsConfig)
        let wrapRequestList = []
        for (let historicalDate of historicalDates) {
            const FINOPS_ACCOUNT_CHARTS_DATE_KEY = this.KEY
            + "-charts"
            + "-date-" + historicalDate.date

            if(!this.metaService.isStale(FINOPS_ACCOUNT_CHARTS_DATE_KEY)) {
                if(isDevMode()) {
                    console.warn("Cache for KEY " + FINOPS_ACCOUNT_CHARTS_DATE_KEY + " still valid, no request executed.")
                }
                continue
            }

            wrapRequestList.push(
                MetaRequestHelper.wrapRequest(
                    this.store,
                    FINOPS_ACCOUNT_CHARTS_DATE_KEY,
                    this.finOpsAwsAccountsService.loadFinOpsAwsAccountsMeasures(historicalDate.date),
                    chart_resources => {
                        ctx.setState(state => {
                            return {
                                ...state,
                                chartsDataByDate: {
                                    ...state.chartsDataByDate,
                                    [historicalDate.date]: chart_resources.measures
                                }
                            }
                        })
                    }
                )
            )
        }
        return forkJoin(wrapRequestList).pipe(
            finalize(() => {
                ctx.dispatch(
                    new LoadFinOpsAwsAccountsChartsFilteredData()
                )
            })
        )
    }

    @Action(LoadFinOpsAwsAccountsChartsFilteredData)
    loadFinOpsAwsAccountsChartsFilteredData(ctx: StateContext<FinOpsAwsAccountsStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig)
        const granularity = chartsConfig.granularity
        const bulkChartsData = ctx.getState().chartsDataByDate
        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.FINOPS_AWS_ACCOUNTS_CHARTS]]

        //Required for charts data computing
        const historicalDatesSelected = this.dateService.getChartsHistoricalDates(chartsConfig).map(item => item.date)
        const computeChartDataLastDate = historicalDatesSelected[historicalDatesSelected.length -1]

        // Init all section
        let historicalChartMinCostRatio: number = 0.0
        let historicalChartMinCostValue: number = 0.0
        let historicalChartCosts: FinOpsChartHistoricalItem[] = []
        let compareMinCostRatio: number = 0.01
        let compareMinCostValue: number = 0.0
        let compareChartCosts: FinOpsChartCompareItem[] = []
        let prediction: boolean = false

        Object.keys(bulkChartsData).sort().forEach( (dateKey) => {
                let filteredAccounts: FinOpsAwsAccount[] = []
                bulkChartsData[dateKey].forEach(measure => {
                    filteredAccounts.push(measure)
                })
                filters.forEach(filter => {
                    if(Array.isArray(filter.value)) {
                        filteredAccounts = filteredAccounts.filter((account) => filter.value.includes(account[filter.key as keyof FinOpsAwsAccount]))
                    } else {
                        filteredAccounts = filteredAccounts.filter((account) => account[filter.key as keyof FinOpsAwsAccount] === filter.value)
                    }
                })

                // Compute historical chart data for the current date
                let totalCosts = 0
                prediction = false
                filteredAccounts.forEach(account => {
                    totalCosts += Number(account.costs_counters[granularity].cost)
                    prediction = account.costs_counters[granularity].prediction
                });

                // Only compute dates concerned by context
                if(historicalDatesSelected.includes(dateKey)) {
                    historicalChartCosts.push(
                        {
                            date: new Date(dateKey),
                            cost: parseFloat(this.floorPipe.transform(totalCosts, 2)),
                            dateString: this.dateService.formatLongDate(dateKey),
                            predictionString: prediction ? this.translate.instant("FINOPS.PREDICTION") + "\n\n" : ""
                        }
                    )
                }

                // Compute compare charts data only for the last date
                if (dateKey == computeChartDataLastDate) {
                    compareMinCostValue = totalCosts * compareMinCostRatio
                    filteredAccounts.forEach(account => {
                        let accountCost = Number(account.costs_counters[granularity].cost)
                        if (accountCost > compareMinCostValue) {
                            let colorKey = 'charts_color_' + compareChartCosts.length
                            compareChartCosts.push(
                                {
                                    category: this.awsAccountLabelPipe.transform(account.id),
                                    cost: parseFloat(this.floorPipe.transform(accountCost, 2)),
                                    color: ColorEnum[colorKey as keyof typeof ColorEnum]
                                }
                            )
                        }
                    });
                }
            }
        )

        ctx.patchState({
            chartsDataDisplayed: {
                filteredHistoricalChartData: {
                    minCostRatioString: historicalChartMinCostRatio == 0 ? "" : this.numberFormatPipe.transform(this.floorPipe.transform(historicalChartMinCostRatio * 100, 2))!,
                    minCostValueString: historicalChartMinCostValue == 0 ? "" : this.numberFormatPipe.transform(this.floorPipe.transform(historicalChartMinCostValue, 2))!,
                    prediction: prediction,
                    costs: historicalChartCosts
                },
                filteredCompareChartData: {
                    minCostRatioString: compareMinCostRatio == 0 ? "" : this.numberFormatPipe.transform(this.floorPipe.transform(compareMinCostRatio * 100, 2))!,
                    minCostValueString: compareMinCostValue == 0 ? "" : this.numberFormatPipe.transform(this.floorPipe.transform(compareMinCostValue, 2))!,
                    dateString: this.dateService.formatLongDate(computeChartDataLastDate),
                    prediction: prediction,
                    costs: compareChartCosts
                }
            }
        })
    }
}
